// @ts-check

/**
 * @file Hook that resolves the active board theme palette for any
 * screen-side consumer. Mirrors `BoardLayout`'s resolution order
 * (persisted user choice → OS preference) without the layout-local
 * transient override; consumers re-render automatically because
 * `useUser()` is context-backed and `updateCurrentUser` runs the
 * moment the user toggles the theme in `BoardLayout`.
 */

import {useUser} from "../auth/use-current-user"
import {buildBoardThemeColors, detectSystemTheme} from "./theme"

/** @import {BoardThemeColors, BoardThemeName} from "./theme" */

/**
 * Resolves the active theme name from the current user, falling
 * back to the OS preference when the user has not picked one.
 * @returns {BoardThemeName} Active theme name.
 */
export function useThemeName() {
  const user = useUser()
  const persisted = user?.theme

  if (persisted === "dark" || persisted === "light") return persisted

  return detectSystemTheme()
}

/**
 * Returns the active theme palette. The returned object is wrapped
 * in `fetching-object` (see `buildBoardThemeColors`), so reading a
 * token that does not exist throws instead of silently rendering
 * `undefined` into a style.
 * @returns {BoardThemeColors} Active theme palette.
 */
export default function useThemeColors() {
  return buildBoardThemeColors(useThemeName())
}
